import { ApolloClient } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from '@apollo/client/link/error';
import { toast } from 'react-toastify';
import { get, omit } from 'lodash';
import { cache } from './cache';
import { convertKeysToCamelCase, getAPIURL, reportError } from '../utils/helpers';
import history from '../utils/browser_history';
import CustomNotification from '../components/CustomNotification';
import { navigationService } from './navigation.service';
import { NO_LOGIN_REDIRECT_PAGES, SPAM_ALERTS } from '../constants/common.constants';
import { HIDDEN_FIELDS, IGNORED_QUERIES } from './utils';

const extensions = {
  SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  NOT_FOUND: 'NOT_FOUND',
  NOT_ALLOWED: 'NOT_ALLOWED',
};

function showErrorNotification(
  message = 'Something went wrong. Please contact support.'
) {
  toast.error(({ toastProps }) => (
    <CustomNotification type={toastProps.type} message={message} />
  ));
}

const onErrorLink = onError(({ response, graphQLErrors, networkError, operation }) => {
  const operationVars = omit(operation.variables, HIDDEN_FIELDS);
  const isMutation = operation.query.definitions.some(
    (def) => def.operation === 'mutation'
  );

  if (graphQLErrors && !IGNORED_QUERIES.includes(operation.operationName)) {
    const err = graphQLErrors[0];
    const errorString = `[${err?.extensions?.code || 'UNHANDLED_ERROR'}] Operation: ${
      operation.operationName
    }, Variables: ${JSON.stringify(operationVars)}, Message: ${err.message}`;

    // adding this because this is an exception for the graphQL error messages where we do not want to show an error toast
    const isResetPassword = operation.operationName === 'sendResetPasswordInstructions';

    switch (err?.extensions?.code) {
    case extensions.NOT_FOUND: {
      if (!isResetPassword)
        if (isMutation) showErrorNotification();
        else navigationService.navigate('/page-not-found');
      break;
    }
    case extensions.NOT_ALLOWED: {
      if (isMutation) showErrorNotification(err.message);
      else navigationService.navigate('/access-denied');
      break;
    }
    case extensions.VALIDATION_ERROR: {
      const parsedErrors = JSON.parse(err.message);
      response.errors = convertKeysToCamelCase(parsedErrors);
      break;
    }
    case extensions.ALREADY_EXISTS: {
      const parsedErrors = JSON.parse(err.message);
      showErrorNotification(get(parsedErrors, 'base[0]', false));
      break;
    }
    case extensions.AUTHENTICATION_ERROR: {
      // Redirect to login page if user is not authenticated
      const currentPath = history.location.pathname;
      const shouldRedirect = !NO_LOGIN_REDIRECT_PAGES.some((page) =>
        currentPath.includes(page)
      );
      if (shouldRedirect) {
        navigationService.navigate(`/login?r=${history.location.pathname}`);
      }
      break;
    }
    case extensions.SERVER_ERROR: {
      reportError(errorString);

      if (isMutation) showErrorNotification();
      else navigationService.navigate('/error');
      break;
    }
    default: {
      reportError(errorString);

      if (isMutation) showErrorNotification();
      else navigationService.navigate('/error');
    }
    }
  } else if (networkError) {
    const errorString = `[NETWORK_ERROR] Operation: ${
      operation.operationName
    }, Variables: ${JSON.stringify(operationVars)},  Message: ${networkError.message}`;
    if (SPAM_ALERTS.includes(networkError.message)) {
      navigationService.rollbar.info(errorString, networkError);
    } else {
      reportError(errorString);
    }
    if (isMutation) showErrorNotification();
    else navigationService.navigate('/error');
  }
});

const httpLink = createUploadLink({
  uri: `${getAPIURL()}/graphql`,
  credentials: 'include',
});

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
};

const client = new ApolloClient({
  cache,
  link: onErrorLink.concat(httpLink),
  defaultOptions,
});

export default client;
